import { useMemo } from 'react';
import type { Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { t } from '@app/i18n';
import { useLocale } from '@app/i18n/useLocale';
import type { AdmissionSettings } from './SiteAdmissionModel';

/**
 * Validação da página «Admissão no cluster». Cobre o que a pessoa digita errado com frequência (host, porta,
 * caminho); o resto — PEM, quantidades, durações, portas do Istio — é do servidor, que repete a validação do
 * admission e devolve o campo exato (`settings.<caminho>`). Duas cópias da mesma regra de PEM aqui e lá
 * divergiriam no primeiro certificado com cadeia.
 */
const HOST = /^[A-Za-z0-9]([A-Za-z0-9.-]{0,251}[A-Za-z0-9])?$/;
const PATH = /^\/[A-Za-z0-9._~/-]*$/;

function port(required = true) {
  const base = yup
    .number()
    .typeError(t('field.portInvalid'))
    .integer(t('field.portInvalid'))
    .min(1, t('field.portInvalid'))
    .max(65535, t('field.portInvalid'));
  return required ? base.required(t('field.portInvalid')) : base;
}

export function admissionSchema() {
  return yup.object({
    gateway: yup.object({
      host: yup.string().trim().required(t('field.required')).matches(HOST, t('field.hostInvalid')),
      port: port(),
      base_path: yup
        .string()
        .trim()
        .test('path', t('field.urlPathInvalid'), (value) => !value || PATH.test(value)),
    }),
    sts: yup.object({
      enabled: yup.boolean(),
      host: yup.string().when('enabled', {
        is: true,
        then: (schema) => schema.trim().required(t('field.required')).matches(HOST, t('field.hostInvalid')),
      }),
      port: yup.number().when('enabled', { is: true, then: () => port() }),
      path: yup.string().when('enabled', {
        is: true,
        then: (schema) => schema.trim().required(t('field.required')).matches(PATH, t('field.urlPathInvalid')),
      }),
      timeout_ms: yup.number().when('enabled', {
        is: true,
        then: (schema) =>
          schema.typeError(t('field.numberMustBePositive')).integer().min(1, t('field.numberMustBePositive')),
      }),
      peer_spiffe_id: yup.string().when('enabled', {
        is: true,
        then: (schema) =>
          schema
            .trim()
            .required(t('field.required'))
            .matches(/^spiffe:\/\//, t('field.spiffeIdInvalid')),
      }),
    }),
    signatures: yup.object({
      enforce: yup.boolean(),
      image_globs: yup.array(yup.string()).when('enforce', {
        is: true,
        then: (schema) => schema.min(1, t('field.signatureNeedsGlobs')),
      }),
      certificates: yup.array(yup.string()).when('enforce', {
        is: true,
        then: (schema) => schema.min(1, t('field.signatureNeedsCertificates')),
      }),
    }),
    advanced: yup.object({
      // J7: sem registry do produto configurado, a imagem chega vazia e é a pessoa quem a informa.
      helper: yup.object({ image: yup.string().trim().required(t('field.required')), health_port: port() }),
      proxy: yup.object({
        image: yup.string().trim().required(t('field.required')),
        listen_port: port(),
        health_port: port(),
      }),
    }),
  });
}

export function useAdmissionValidation() {
  // O idioma é dependência: as frases são montadas na construção do schema (mesma razão de `useSiteValidation`).
  const locale = useLocale();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => admissionSchema(), [locale]);
  return {
    // O schema cobre um subconjunto do objeto: o resolvedor tipado pelo schema não conhece os outros campos.
    resolver: yupResolver(schema) as unknown as Resolver<AdmissionSettings>,
    mode: 'onChange' as const,
  };
}
